import config from "@/App/config";
import { HttpClient } from "@/App/services/HttpClient";
import { AuthorizationService, IUser } from "@/Login/services";
import type { Exchange } from "./types/Exchange";
import { EmtLevel, QuestionParams } from "./types/QuestionParams";
import { Storage } from "@/App/services/LocalStorage";

const TWIAGE_API_URL = config.API_URL;

export type StreamCallback = (textResponse: string, isComplete: boolean, finalData?: Exchange) => void;

export const askQuestion = ({
  data,
  onStream,
  skipStreaming,
}: {
  data: Partial<QuestionParams>;
  onStream: StreamCallback;
  skipStreaming?: boolean;
}): Promise<Exchange> => {
  return streamingRequest(data, onStream, skipStreaming);
};

export const questionFeedback = ({ data }: { data: Record<string, unknown> }) => {
  return HttpClient.post(`${TWIAGE_API_URL}/feedback/`, data);
};

const streamingRequest = async (
  data: Partial<QuestionParams>,
  onStream: StreamCallback,
  skipStreaming = false,
): Promise<Exchange> => {
  const token = AuthorizationService.getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `JWT ${token}` : "",
  };

  if (!skipStreaming) {
    headers.streaming = "true";
  }

  const response = await fetch(`${TWIAGE_API_URL}/questions/`, {
    method: "POST",
    body: JSON.stringify(data),
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  if (skipStreaming) {
    const jsonData = await response.json();
    onStream("", true, jsonData);
    return jsonData;
  }

  if (!response.body) {
    throw new Error("ReadableStream not supported");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let finalExchange: Exchange | undefined;

  const { value } = await reader.read();

  const chunk = decoder.decode(value);

  if (chunk.includes("+++")) {
    const parts = chunk.split("+++");
    const textPart = parts[0].trim();

    let accumulatedText = "";
    for (const char of textPart) {
      accumulatedText += char;
      await new Promise((resolve) => setTimeout(resolve, 0.1));
      onStream(accumulatedText, false);
    }

    if (parts.length > 1 && parts[1].trim()) {
      try {
        const jsonPart = JSON.parse(parts[1].trim());
        finalExchange = jsonPart;

        onStream(textPart, true, jsonPart);
      } catch (e) {
        console.error("Error parsing JSON part:", e);
        onStream(textPart, true);
        finalExchange = createBasicExchange(data, textPart);
      }
    } else {
      onStream(textPart, true);
      finalExchange = createBasicExchange(data, textPart);
    }
  } else {
    onStream(chunk, true);
    finalExchange = createBasicExchange(data, chunk);
  }

  reader.cancel();

  return finalExchange as Exchange;
};

const createBasicExchange = (data: Partial<QuestionParams>, text: string): Exchange => {
  return {
    question: data.question,
    questionDatetime: new Date(),
    summarizedResult: text,
  };
};

const getEmTLevelKey = (id: string) => {
  return `emtLevel-${id}`;
};

export const getEmtLevel = (user: IUser | null) => {
  if (!user?.id) return null;
  const storedEmtLevel = Storage.getItem(getEmTLevelKey(user.id));
  return storedEmtLevel ? storedEmtLevel : null;
};

export const setEmtLevel = (user: IUser | null, emtLevel: EmtLevel) => {
  if (!user?.id) return;
  Storage.setItem(getEmTLevelKey(user.id), emtLevel);
};
